import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { MapPin, PenTool, Award, TrendingUp, Blocks, ArrowRight, MessageCircle } from "lucide-react";

const pillars = [
  { icon: MapPin, title: "Local KL Technical Team", desc: "Based at Menara Keck Seng, Bukit Bintang — fast site visits across the Klang Valley and nationwide coverage." },
  { icon: PenTool, title: "Custom System Design", desc: "No cookie-cutter packages. Every solution is engineered around your floor plan, risks and budget." },
  { icon: Award, title: "OEM / Certified Hardware", desc: "We supply genuine, warranty-backed equipment from leading global security and AV manufacturers." },
  { icon: TrendingUp, title: "High ROI & Scalability", desc: "Systems designed to grow with you — start with one floor, expand to the whole building without rework." },
  { icon: Blocks, title: "Seamless Integration", desc: "CCTV, access control, intercom, PA and network unified on one manageable platform." },
];

export default function WhyChooseUs() {
  return (
    <div data-testid="why-choose-us-page">
      <PageHero
        label="Why Choose Us"
        title="Six Reasons Businesses Choose YC SYSTEM"
        subtitle="We combine trading expertise with genuine engineering capability — so you get the right equipment, installed right, supported for the long run."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="glow-card p-8"
                data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-12 h-12 bg-blue-600 flex items-center justify-center border border-cyan-400/40 mb-5">
                  <p.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glow-card mt-14 p-8 lg:p-12"
            data-testid="process-card"
          >
            <span className="tech-label">How We Work</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white mt-2 mb-8">
              From Site Survey to Handover
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: "01", t: "Site Survey", d: "We inspect your premises and map risks, coverage zones and cable routes." },
                { step: "02", t: "Design & Quote", d: "Itemised proposal with equipment specs, layout drawings and transparent pricing." },
                { step: "03", t: "Installation", d: "Certified installation with minimal disruption to your operations." },
                { step: "04", t: "Handover & Support", d: "Testing, training, documentation — then ongoing maintenance and support." },
              ].map((s) => (
                <div key={s.step} className="border-l-2 border-cyan-400/40 pl-4" data-testid={`process-step-${s.step}`}>
                  <div className="font-tech text-cyan-400 text-sm">{s.step}</div>
                  <div className="font-display text-white font-semibold mt-1">{s.t}</div>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/quote"
              data-testid="why-quote-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors border border-cyan-400/40"
            >
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://wa.me/601172283321"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-whatsapp-cta"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#25D366]/60 text-white font-semibold hover:bg-[#25D366]/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
